import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent, ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * Event fired when variant validation is performed
 * @extends {CustomEvent<{ result: { valid: boolean, unselectedPositions: number[] } }>}
 */
export class VariantValidationEvent extends CustomEvent {
  /**
   * Creates a new VariantValidationEvent
   * @param {{ valid: boolean, unselectedPositions: number[] }} result - Validation result
   */
  constructor(result) {
    super(ThemeEvents.variantValidation, { bubbles: true, detail: { result } });
  }
}

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {(event: Event) => void} */
  #documentClickHandler = this.#handleDocumentClick.bind(this);

  /**
   * Parses a comma-separated data attribute into an array of integers.
   * @param {string} attr - camelCase dataset key (e.g. 'unselectedPositions')
   * @returns {number[]}
   */
  #parsePositions(attr) {
    const raw = this.dataset[attr];
    return raw ? raw.split(',').map((p) => parseInt(p.trim(), 10)) : [];
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.variantChanged.bind(this));

    this.addEventListener('click', this.#handleDropdownClick.bind(this));
    this.addEventListener('keydown', this.#handleDropdownKeydown.bind(this));
    this.addEventListener('focusout', this.#handleFocusOut.bind(this));
    document.addEventListener('click', this.#documentClickHandler, true);

    this.#validateConfiguration();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#documentClickHandler, true);
  }

  /**
   * Handles click events on the custom dropdown trigger.
   * @param {MouseEvent} event
   */
  #handleDropdownClick(event) {
    const trigger = /** @type {HTMLElement} */ (event.target)?.closest('.variant-option__select-trigger');
    if (!trigger) return;

    const wrapper = trigger.closest('[data-custom-select]');
    if (!wrapper) return;

    const isOpen = trigger.getAttribute('aria-expanded') === 'true';
    this.#closeAllDropdowns();

    if (!isOpen) {
      const panel = wrapper.querySelector('.variant-option__select-panel');
      if (panel) {
        panel.removeAttribute('hidden');
        trigger.setAttribute('aria-expanded', 'true');
      }
    }
  }

  /**
   * Closes all custom dropdowns when clicking outside the variant picker.
   * @param {Event} event
   */
  #handleDocumentClick(event) {
    if (!this.contains(/** @type {Node} */ (event.target))) {
      this.#closeAllDropdowns();
    }
  }

  /**
   * Closes open dropdowns when focus leaves this component entirely.
   * @param {FocusEvent} event
   */
  #handleFocusOut(event) {
    if (event.relatedTarget && !this.contains(/** @type {Node} */ (event.relatedTarget))) {
      this.#closeAllDropdowns();
    }
  }

  /**
   * Full keyboard navigation for custom dropdowns.
   *
   * On the trigger:
   *   Enter / Space / ArrowDown — open (if closed) and focus selected-or-first option
   *   ArrowUp                   — open (if closed) and focus last option
   *   Escape                    — close
   *
   * On an option:
   *   ArrowDown / ArrowUp  — move between options (wraps)
   *   Home / End           — jump to first / last option
   *   Enter / Space        — select option, return focus to trigger
   *   Escape               — close, return focus to trigger
   *   Tab                  — close, let browser move focus naturally
   *
   * @param {KeyboardEvent} event
   */
  #handleDropdownKeydown(event) {
    const trigger = /** @type {HTMLElement | null} */ (
      /** @type {HTMLElement} */ (event.target)?.closest('.variant-option__select-trigger')
    );
    const option = /** @type {HTMLElement | null} */ (
      /** @type {HTMLElement} */ (event.target)?.closest('.variant-option__select-option')
    );
    const wrapper = /** @type {HTMLElement | null} */ ((trigger ?? option)?.closest('[data-custom-select]'));

    if (!wrapper) {
      if (event.key === 'Escape') this.#closeAllDropdowns();
      return;
    }

    const panel = /** @type {HTMLElement | null} */ (wrapper.querySelector('.variant-option__select-panel'));

    if (trigger) {
      switch (event.key) {
        case 'ArrowDown': {
          event.preventDefault();
          if (panel?.hasAttribute('hidden')) {
            panel.removeAttribute('hidden');
            trigger.setAttribute('aria-expanded', 'true');
          }
          this.#focusDropdownOption(wrapper, 'selected-or-first');
          break;
        }
        case 'ArrowUp': {
          event.preventDefault();
          if (panel?.hasAttribute('hidden')) {
            panel.removeAttribute('hidden');
            trigger.setAttribute('aria-expanded', 'true');
          }
          this.#focusDropdownOption(wrapper, 'last');
          break;
        }
        case 'Enter':
        case ' ': {
          event.preventDefault();
          const isOpen = trigger.getAttribute('aria-expanded') === 'true';
          if (isOpen) {
            this.#closeAllDropdowns();
          } else {
            panel?.removeAttribute('hidden');
            trigger.setAttribute('aria-expanded', 'true');
            this.#focusDropdownOption(wrapper, 'selected-or-first');
          }
          break;
        }
        case 'Escape':
          this.#closeAllDropdowns();
          break;
      }
      return;
    }

    if (option) {
      const options = /** @type {HTMLElement[]} */ (
        Array.from(wrapper.querySelectorAll('.variant-option__select-option'))
      );
      const currentIndex = options.indexOf(option);

      switch (event.key) {
        case 'ArrowDown': {
          event.preventDefault();
          options[(currentIndex + 1) % options.length]?.focus();
          break;
        }
        case 'ArrowUp': {
          event.preventDefault();
          options[(currentIndex - 1 + options.length) % options.length]?.focus();
          break;
        }
        case 'Home':
          event.preventDefault();
          options[0]?.focus();
          break;
        case 'End':
          event.preventDefault();
          options[options.length - 1]?.focus();
          break;
        case 'Enter':
        case ' ': {
          event.preventDefault();
          const focusTrigger = /** @type {HTMLElement | null} */ (
            wrapper.querySelector('.variant-option__select-trigger')
          );
          option.click();
          focusTrigger?.focus();
          break;
        }
        case 'Escape': {
          const focusTrigger = /** @type {HTMLElement | null} */ (
            wrapper.querySelector('.variant-option__select-trigger')
          );
          this.#closeAllDropdowns();
          focusTrigger?.focus();
          break;
        }
        case 'Tab':
          this.#closeAllDropdowns();
          break;
      }
    }
  }

  /**
   * Moves focus to an option inside an open dropdown panel.
   * @param {HTMLElement} wrapper - The [data-custom-select] wrapper element.
   * @param {'selected-or-first' | 'last'} [which='selected-or-first'] - Which option to focus.
   */
  #focusDropdownOption(wrapper, which = 'selected-or-first') {
    const options = /** @type {HTMLElement[]} */ (
      Array.from(wrapper.querySelectorAll('.variant-option__select-option'))
    );
    if (!options.length) return;

    if (which === 'last') {
      options[options.length - 1]?.focus();
      return;
    }

    const selected = options.find((el) => el.querySelector('input:checked'));
    (selected ?? options[0])?.focus();
  }

  /**
   * Closes all open custom dropdown panels inside this variant picker.
   */
  #closeAllDropdowns() {
    this.querySelectorAll('.variant-option__select-panel').forEach((panel) => {
      panel.setAttribute('hidden', '');
    });
    this.querySelectorAll('.variant-option__select-trigger').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
    });
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const input = event.target.querySelector('input') || event.target;
    if (!(input instanceof HTMLInputElement)) return;

    this.updateSelectedOption(input);

    const optionGroup = /** @type {HTMLElement | null} */ (input.closest('[data-option-position]'));

    if (optionGroup && input.dataset.isPlaceholder !== 'true') {
      optionGroup.classList.remove('variant-option--error');
      optionGroup.removeAttribute('aria-invalid');

      const value = input.value || input.getAttribute('aria-label') || '';
      this.#updateSwatchValue(optionGroup, value);
    }

    if (optionGroup && input.dataset.isPlaceholder === 'true') {
      const placeholderText = optionGroup.dataset.placeholderText || 'Select...';
      this.#updateSwatchValue(optionGroup, placeholderText);
    }

    const panel = input.closest('.variant-option__select-panel');
    if (panel) {
      const wrapper = /** @type {HTMLElement | null} */ (panel.closest('[data-custom-select]'));
      if (wrapper) {
        const value = input.dataset.isPlaceholder === 'true'
          ? (optionGroup?.dataset.placeholderText || 'Select...')
          : input.value;
        this.#updateDropdownTrigger(wrapper, value);
      }
      this.#closeAllDropdowns();
    }

    const validationResult = this.validateRequiredOptions();
    this.dispatchEvent(new VariantValidationEvent(validationResult));

    this.dispatchEvent(
      new VariantSelectedEvent({
        id: input.dataset.optionValueId ?? '',
        variantId: input.dataset.variantId,
      })
    );

    const target = /** @type {HTMLElement} */ (event.target);
    const isOnProductPage =
      Theme.template.name === 'product' &&
      !target.closest('product-card') &&
      !target.closest('quick-add-dialog');

    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = target.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(target), loadsNewProduct);

    const url = new URL(window.location.href);

    if (isOnProductPage) {
      if (target.dataset.variantId) {
        url.searchParams.set('variant', target.dataset.variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    if (loadsNewProduct) {
      url.pathname = /** @type {string} */ (newUrl);
    }

    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    // Get all selected option values, including real selections from unconfigured positions
    // and placeholder IDs for unselected positions
    const selectedValues = this.#getAllSelectedOptionValues();

    if (selectedValues.length && !source) {
      params.push(`option_values=${selectedValues.join(',')}`);
    } else if (source === 'product-card') {
      if (sourceSelectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    // The server always renders unselected positions with the placeholder
    // checked, so a plain morph would reset any user selection back to the
    // placeholder. We swap in clones of the live DOM (with synced checked
    // state + server availability) so the morph sees no diff for those
    // groups.
    this.#preserveUnselectedGroupsInSource(newVariantPickerSource);

    morph(this, newVariantPickerSource);

    // Re-validate after morph so the ATC button reflects the current
    // (possibly still incomplete) selection state.
    const unselectedPositions = this.dataset.unselectedPositions;
    if (unselectedPositions) {
      const result = this.validateRequiredOptions();
      this.dispatchEvent(new VariantValidationEvent(result));
    }

    return newProduct;
  }

  /**
   * Patches `newSource` so that the morph preserves unselected-position
   * selection state while still allowing the server-rendered availability
   * and variant-ID data to flow through.
   *
   * The server always renders unselected positions with the placeholder
   * checked, regardless of what the user has selected client-side. Without
   * intervention the morph would reset any real value the user picked back
   * to the placeholder. We swap in a clone of the current DOM group
   * (with synced checked state and server availability data) so the morph
   * sees no diff and leaves these groups untouched.
   *
   * @param {Element} newSource - The new variant-picker element (mutated).
   */
  #preserveUnselectedGroupsInSource(newSource) {
    const positions = this.#parsePositions('unselectedPositions');
    if (!positions.length) return;

    for (const position of positions) {
      const currentGroup = /** @type {HTMLElement | null} */ (
        this.querySelector(`[data-option-position="${position}"]`)
      );
      if (!currentGroup) continue;

      const newGroup = /** @type {HTMLElement | null} */ (
        newSource.querySelector(`[data-option-position="${position}"]`)
      );
      if (!newGroup) continue;

      // Clone the current DOM group to preserve the user's selection
      // state. The server always renders unselected positions with the
      // placeholder checked, so without this the morph would reset any
      // real value the user has already picked back to placeholder.
      const clone = /** @type {HTMLElement} */ (currentGroup.cloneNode(true));

      // cloneNode copies HTML attributes but not live property values.
      // Radio `.checked` properties diverge from the `checked` attribute
      // after user interaction, so we must manually sync them.
      this.#syncCheckedState(currentGroup, clone);

      this.#syncAvailabilityData(newGroup, clone);

      newGroup.parentNode?.replaceChild(clone, newGroup);
    }
  }

  /**
   * Syncs the live `.checked` property of radio inputs from the source
   * element onto the corresponding inputs in the clone. `cloneNode` only
   * copies the HTML `checked` attribute, which diverges from the property
   * after user interaction.
   *
   * @param {Element} source - The live DOM element.
   * @param {Element} clone  - The deep-cloned element.
   */
  #syncCheckedState(source, clone) {
    const sourceInputs = source.querySelectorAll('input[type="radio"]');
    const cloneInputs = clone.querySelectorAll('input[type="radio"]');

    for (let i = 0; i < sourceInputs.length && i < cloneInputs.length; i++) {
      const src = /** @type {HTMLInputElement} */ (sourceInputs[i]);
      const dst = /** @type {HTMLInputElement} */ (cloneInputs[i]);
      dst.checked = src.checked;

      // Also sync the attribute to match the property so the morph's
      // `updateInput` sees no diff.
      if (src.checked) {
        dst.setAttribute('checked', '');
      } else {
        dst.removeAttribute('checked');
      }
    }
  }

  /**
   * Copies `data-option-available`, `data-variant-id`, and related
   * attributes from server-rendered inputs onto matching inputs in the
   * clone, and updates unavailable CSS classes on dropdown labels.
   *
   * @param {Element} serverGroup - The server-rendered option group.
   * @param {Element} clonedGroup - The cloned current-DOM option group.
   */
  #syncAvailabilityData(serverGroup, clonedGroup) {
    const serverInputs = serverGroup.querySelectorAll(
      'input[data-option-value-id]:not([data-is-placeholder])'
    );

    for (const serverInput of serverInputs) {
      if (!(serverInput instanceof HTMLInputElement)) continue;

      const valueId = serverInput.dataset.optionValueId;
      if (!valueId) continue;

      const cloneInput = /** @type {HTMLInputElement | null} */ (
        clonedGroup.querySelector(`input[data-option-value-id="${CSS.escape(valueId)}"]`)
      );
      if (!cloneInput) continue;

      cloneInput.dataset.optionAvailable = serverInput.dataset.optionAvailable ?? 'true';

      if (serverInput.dataset.variantId) {
        cloneInput.dataset.variantId = serverInput.dataset.variantId;
      } else {
        delete cloneInput.dataset.variantId;
      }

      if (serverInput.dataset.connectedProductUrl) {
        cloneInput.dataset.connectedProductUrl = serverInput.dataset.connectedProductUrl;
      }

      // Update visual unavailable state on parent label (for both
      // button-style and dropdown-style variants)
      const cloneLabel = cloneInput.closest('label');
      const serverLabel = serverInput.closest('label');
      if (cloneLabel && serverLabel) {
        const unavailableClass = 'variant-option__select-option--unavailable';
        cloneLabel.classList.toggle(
          unavailableClass,
          serverLabel.classList.contains(unavailableClass)
        );
      }
    }
  }

  /**
   * Gets all selected option values including those from unselected positions.
   * For unselected positions with placeholders, uses the first real value's ID
   * to ensure proper variant matching on the server.
   * @returns {string[]} Array of option value IDs
   */
  #getAllSelectedOptionValues() {
    /** @type {string[]} */
    const values = [];
    const unselectedSet = new Set(this.#parsePositions('unselectedPositions'));

    // Get all option groups
    const optionGroups = this.querySelectorAll('[data-option-position]');

    optionGroups.forEach((group) => {
      if (!(group instanceof HTMLElement)) return;
      const position = parseInt(group.dataset.optionPosition || '0', 10);
      const checkedInput = group.querySelector('input:checked');

      if (!checkedInput || !(checkedInput instanceof HTMLInputElement)) return;

      if (unselectedSet.has(position) && checkedInput.dataset.isPlaceholder === 'true') {
        // For placeholder-selected positions, send a real option value for server-side
        // variant resolution. Prefer an available value and only fall back to first real value.
        const realInputs = Array.from(group.querySelectorAll('input:not([data-is-placeholder])')).filter(
          (el) => el instanceof HTMLInputElement
        );
        const firstAvailableInput = realInputs.find((el) => el.dataset.optionAvailable !== 'false');
        const fallbackInput = firstAvailableInput ?? realInputs[0];

        if (fallbackInput && fallbackInput.dataset.optionValueId) {
          values.push(fallbackInput.dataset.optionValueId);
        }
      } else if (checkedInput.dataset.optionValueId) {
        // Normal selected value
        values.push(checkedInput.dataset.optionValueId);
      }
    });

    return values;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Validates configuration data attributes against the actual option groups
   * rendered in the DOM. Logs warnings for invalid positions.
   */
  #validateConfiguration() {
    const positions = this.#parsePositions('unselectedPositions');
    if (!positions.length) return;

    const totalOptions = this.querySelectorAll('[data-option-position]').length;
    if (totalOptions === 0) return;

    for (const pos of positions) {
      if (pos > totalOptions) {
        console.warn(
          `[variant-picker] Position ${pos} is greater than available options (${totalOptions}). This position will be ignored.`
        );
      } else if (!this.querySelector(`[data-option-position="${pos}"]`)) {
        console.warn(
          `[variant-picker] Product has no option at position ${pos}. Rendering normally.`
        );
      }
    }
  }

  /**
   * Validates that all required options have been selected.
   * @returns {{valid: boolean, unselectedPositions: number[]}} Validation result
   */
  validateRequiredOptions() {
    const requiredPositions = this.dataset.requiredPositions;
    const unselectedArray = this.#parsePositions('unselectedPositions');
    if (!unselectedArray.length) {
      return { valid: true, unselectedPositions: [] };
    }

    const requiredArray = requiredPositions
      ? this.#parsePositions('requiredPositions')
      : unselectedArray;

    /** @type {number[]} */
    const invalidPositions = [];

    for (const position of requiredArray) {
      const optionGroup = this.querySelector(`[data-option-position="${position}"]`);
      if (!optionGroup) continue;

      const selectedInput = /** @type {HTMLInputElement | null} */ (optionGroup.querySelector('input:checked'));
      if (!selectedInput || selectedInput.dataset.isPlaceholder === 'true') {
        invalidPositions.push(position);
      }
    }

    return {
      valid: invalidPositions.length === 0,
      unselectedPositions: invalidPositions,
    };
  }

  /**
   * Shows error states on unselected required options.
   * @param {number[]} positions - Array of option positions to mark as errored
   */
  showErrorStates(positions) {
    positions.forEach((position) => {
      const optionGroup = this.querySelector(`[data-option-position="${position}"]`);
      if (optionGroup) {
        optionGroup.classList.add('variant-option--error');
        optionGroup.setAttribute('aria-invalid', 'true');
      }
    });

    const firstError = this.querySelector('.variant-option--error');
    if (firstError) {
      firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  /**
   * Updates the swatch value display for an option.
   * @param {Element} optionGroup - The option fieldset or dropdown container
   * @param {string} value - The new value to display
   */
  #updateSwatchValue(optionGroup, value) {
    const swatchValue = optionGroup.querySelector('[data-swatch-value-container]');
    if (swatchValue) {
      swatchValue.textContent = value;
    }
  }

  /**
   * Updates the dropdown trigger text.
   * @param {Element} wrapper - The custom select wrapper
   * @param {string} value - The new value to display
   */
  #updateDropdownTrigger(wrapper, value) {
    const currentLabel = wrapper.querySelector('[data-select-current], .variant-option__select-current');
    if (currentLabel) {
      currentLabel.textContent = value;
    }
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector(
      'select option[selected], fieldset input:checked, [data-custom-select] input:checked'
    );

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(
      this.querySelectorAll('select option[selected], fieldset input:checked, [data-custom-select] input:checked')
    );

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
